/**
 * DealPackage calibration / cfScale fidelity diagnostics.
 * Streams are decline-reconstructed then scaled to seller oneline PV10 —
 * this surfaces how much of the model is physics vs fit.
 */

function median(sorted) {
  if (!sorted.length) return null;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * @param {object} pkg DealPackage
 * @returns {object|null} summary for UI / freeze / IC memo
 */
export function summarizeCalibration(pkg) {
  if (!pkg?.assets?.length) return null;
  const scales = pkg.assets
    .map((a) => ({
      lease: a.lease,
      propnum: a.propnum,
      resCat: a.resCat,
      isBoswell: !!a.isBoswell,
      cfScale: a.cfScale ?? 1,
      nri: a.nri,
      wi: a.wi,
      deAnnual: a.deAnnual,
      qiMcfd: a.qiMcfd,
      refPv10MM: a.ref?.pv10 != null ? a.ref.pv10 / 1e6 : null,
    }))
    .filter((a) => Number.isFinite(a.cfScale));

  const vals = scales.map((a) => a.cfScale).sort((a, b) => a - b);
  const boswell = scales.find((a) => a.isBoswell);
  const outliers = scales
    .filter((a) => Math.abs(a.cfScale - 1) > 0.3)
    .sort((a, b) => Math.abs(b.cfScale - 1) - Math.abs(a.cfScale - 1));

  const v = pkg.validation || {};
  return {
    streamMethod: pkg.meta?.streamMethod || null,
    generatorVersion: pkg.meta?.generatorVersion || null,
    refPdpPv10MM: v.refPdpPv10MM ?? null,
    refTotalPv10MM: v.refTotalPv10MM ?? null,
    modelBasePv10MM: v.modelBasePv10MM ?? null,
    modelVsRefPdpMM:
      v.modelBasePv10MM != null && v.refPdpPv10MM != null
        ? +(v.modelBasePv10MM - v.refPdpPv10MM).toFixed(3)
        : null,
    assetCount: scales.length,
    cfScale: {
      min: vals.length ? +vals[0].toFixed(4) : null,
      median: vals.length ? +median(vals).toFixed(4) : null,
      max: vals.length ? +vals[vals.length - 1].toFixed(4) : null,
      boswell: boswell ? +boswell.cfScale.toFixed(4) : null,
      outlierCount: outliers.length,
      outlierThreshold: 0.3,
    },
    boswell: boswell
      ? {
          lease: boswell.lease,
          nri: boswell.nri,
          wi: boswell.wi,
          deAnnual: boswell.deAnnual,
          qiMcfd: boswell.qiMcfd,
          cfScale: +boswell.cfScale.toFixed(4),
          refPv10MM: boswell.refPv10MM,
        }
      : null,
    topOutliers: outliers.slice(0, 8).map((a) => ({
      lease: a.lease,
      propnum: a.propnum,
      cfScale: +a.cfScale.toFixed(3),
      refPv10MM: a.refPv10MM != null ? +a.refPv10MM.toFixed(3) : null,
      nri: a.nri,
    })),
  };
}
